/**
 * Messages in the customer portal (tracker item 56, phase 3): the signed-in
 * customer's own thread with the office, oldest first; a reply box; office
 * messages show the staff member's first name only. Opening the page marks
 * the office's messages read (the nav badge clears). The office's notice
 * email links here (APP_PUBLIC_URL/portal/messages). Linked logins only (the
 * shell sends an unlinked login home). Message text is plain text only.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createAPIClient, IConversationsApi } from '../../api';
import * as types from '../../api/types';
import { primaryButtonClass } from '../../components/formStyles';
import { describePortalFailure } from '../../utils/portal';
import { usePortal } from './PortalContext';
import { EmptyBlock, FailureBlock, InlineAlert, LoadingBlock } from './PortalStates';
import { usePortalLoad } from './usePortalLoad';

export const PORTAL_MESSAGES_PATH = '/portal/messages';
export const PORTAL_MESSAGES_MAX = 5000;

/** Fired after the portal user reads or sends, so the nav badge updates at once. */
export const PORTAL_UNREAD_CHANGED = 'crm:portal-unread-changed';

function notifyPortalUnreadChanged(): void {
  try {
    window.dispatchEvent(new Event(PORTAL_UNREAD_CHANGED));
  } catch {
    // Non-essential
  }
}

function useConversationsApi(): IConversationsApi {
  return useMemo(() => createAPIClient() as IConversationsApi, []);
}

/** The unread office messages for the portal nav badge (0 when unknown or not linked). */
export function usePortalUnreadMessages(linked: boolean, pathname: string): number {
  const api = useConversationsApi();
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!linked) {
      setCount(0);
      return undefined;
    }
    let active = true;
    const load = async () => {
      try {
        const thread = await api.getPortalMessages();
        if (active) setCount(thread.unreadCount);
      } catch {
        if (active) setCount(0);
      }
    };
    void load();
    const onChanged = () => { void load(); };
    window.addEventListener(PORTAL_UNREAD_CHANGED, onChanged);
    return () => {
      active = false;
      window.removeEventListener(PORTAL_UNREAD_CHANGED, onChanged);
    };
  }, [api, linked, pathname]);
  return count;
}

function when(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function PortalMessagesPage() {
  const api = useConversationsApi();
  const { me } = usePortal();
  const { data, loading, failure, reload, setData } = usePortalLoad(() => api.getPortalMessages(), 'We could not load your messages.');
  const [text, setText] = useState('');
  const [sendError, setSendError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const markedFor = useRef<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  // Opening the thread reads the office's messages
  const markRead = useCallback(async (thread: types.PortalThread) => {
    if (!thread.conversationId || thread.unreadCount === 0 || markedFor.current === thread.conversationId + thread.messages.length) return;
    markedFor.current = thread.conversationId + thread.messages.length;
    try {
      await api.markPortalMessagesRead();
      notifyPortalUnreadChanged();
    } catch {
      // The badge catches up on the next page
    }
  }, [api]);

  useEffect(() => {
    if (data) void markRead(data);
  }, [data, markRead]);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: 'end' });
  }, [data?.messages.length]);

  const send = async (event: React.FormEvent) => {
    event.preventDefault();
    const body = text.trim();
    if (!body) return setSendError('Write a message first.');
    if (body.length > PORTAL_MESSAGES_MAX) return setSendError(`Keep your message under ${PORTAL_MESSAGES_MAX} characters.`);
    setSending(true);
    setSendError(null);
    try {
      const thread = await api.sendPortalMessage(body);
      setData(thread);
      setText('');
      notifyPortalUnreadChanged();
    } catch (error) {
      setSendError(describePortalFailure(error, 'Your message was not sent. Please try again.').message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-bold text-gray-900">Messages</h1>
      <p className="text-sm text-gray-600">Write to {me.companyName}. We reply here and let you know by email.</p>

      {loading && !data && <LoadingBlock label="Loading messages" />}
      {failure && <FailureBlock failure={failure} onRetry={() => void reload()} />}

      {data && data.messages.length === 0 && <EmptyBlock>No messages yet. Send us one below.</EmptyBlock>}
      {data && data.messages.length > 0 && (
        <ol className="space-y-3" aria-label="Messages">
          {data.messages.map((m) => (
            <li
              key={m.id}
              data-testid="portal-message"
              data-from={m.from}
              className={`max-w-[90%] rounded-lg p-3 text-sm ring-1 ${m.from === 'CUSTOMER' ? 'ml-auto bg-blue-50 ring-blue-100' : 'bg-white ring-gray-200'}`}
            >
              <p className="text-xs font-semibold text-gray-600">
                {m.from === 'CUSTOMER' ? 'You' : m.authorName ? `${m.authorName} at ${me.companyName}` : me.companyName}
                <span className="ml-2 font-normal text-gray-500">{when(m.createdAt)}</span>
              </p>
              <p className="mt-1 whitespace-pre-wrap break-words text-gray-900">{m.body}</p>
            </li>
          ))}
        </ol>
      )}
      <div ref={endRef} />

      {data && (
        <form onSubmit={(e) => void send(e)} className="space-y-2 rounded-lg bg-white p-3 ring-1 ring-gray-200" aria-label="Send a message">
          <label htmlFor="portal-message-text" className="block text-sm font-semibold text-gray-700">Your message</label>
          <textarea
            id="portal-message-text"
            rows={4}
            maxLength={PORTAL_MESSAGES_MAX}
            className="w-full rounded-md border border-gray-300 p-3 text-base"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          {sendError && <InlineAlert>{sendError}</InlineAlert>}
          <button type="submit" className={`${primaryButtonClass} min-h-[44px] w-full sm:w-auto`} disabled={sending}>
            {sending ? 'Sending…' : 'Send'}
          </button>
        </form>
      )}
    </div>
  );
}
